import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AI_API_URL } from '../../constants';

/**
 * 인기 영상 데이터 관리를 담당하는 커스텀 훅
 * @param {boolean} isLoggedIn - 로그인 상태
 * @param {string} userId - 사용자 ID
 * @param {number} selectedPeriod - 선택된 기간 필터
 * @returns {object} - 인기 영상 관련 상태와 함수들
 */
export const usePopularVideos = (isLoggedIn, userId, selectedPeriod) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  /**
   * 인기 영상 데이터 가져오기
   * @param {number|null} daysFilter - 기간 필터 (null이면 전체)
   */
  const fetchPopularVideos = async (daysFilter = selectedPeriod) => {
    try {
      setLoading(true);
      setError('');
      
      // 로그인하지 않은 경우 임시 userId로 요청 (인기 영상 조회는 로그인 없이도 가능)
      const requestUserId = userId || 'anonymous';
      
      const daysParam = daysFilter ? `&days_filter=${daysFilter}` : '';
      const response = await fetch(`${AI_API_URL}/api/streaming/popular-videos?limit=8&order_by=usageCount${daysParam}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'user-id': requestUserId
        }
      });

      if (response.ok) {
        const data = await response.json();
        setVideos(data.videos || []);
        
        // 캐시 정보 로깅
        if (data.cached) {
          console.log('🚀 [인기 영상] 캐시에서 로딩됨 - 응답속도 향상!');
        } else {
          console.log('💾 [인기 영상] 실시간 조회됨');
        }
      } else {
        console.error('인기 영상 조회 실패:', response.status);
        setError(t('common.popularVideosError'));
      }
    } catch (err) {
      console.error('인기 영상 조회 에러:', err);
      setError('인기 영상을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPopularVideos();
  }, [isLoggedIn, userId, selectedPeriod]);

  return {
    videos,
    loading,
    error,
    fetchPopularVideos
  };
}; 